import React, { useState, useEffect } from "react";
import { Table, Tag, Button, Input, Modal, message, Dropdown, Tooltip } from "antd";
import type { MenuProps } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { ColumnType } from "antd/lib/table";
import { useNavigate } from "react-router-dom";
import DeleteConfirmation from "./DeleteConfirmationModal";
import "./Components.css";
import { Progress } from "antd";
import { getEndpoint } from '../utils/endpoints';
import axios from "axios";
import Papa from "papaparse";

const normalizeDashboardStatus = (status?: string | null) => {
  if (!status) {
    return status;
  }
  const lowered = status.toLowerCase();
  if (lowered === "batch_in_progress_translation") {
    return "translation started";
  }
  return status;
};

interface ChildProps {}

const getDocuments = async (page, pageSize, sortBy, sortOrder, filters) => {
  try {
    const response = await axios.post(
      `${getEndpoint('BACKEND')}/getDocuments/v2/`,
      {
        page,
        pageSize,
        sortBy,
        sortOrder,
        filters,
      },
      {
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching documents:", error);
    throw new Error("Failed to fetch documents.");
  }
};


const Dashboard: React.FC<ChildProps> = ({}) => {
  const [data, setData] = useState<DocumentData>({
    documents: []
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalDocuments, setTotalDocuments] = useState(0); // Total count from API
  const [sortInfo, setSortInfo] = useState({ field: "creation_timestamp", order: "descend" });
  const [filters, setFilters] = useState({});

  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  // Reset translation modal states
  const [isResetModalVisible, setIsResetModalVisible] = useState(false);
  const [currentResetRecord, setCurrentResetRecord] = useState(null);
  const allStatuses = [
    "to be uploaded",
    "ready for translation",
    "downloading",
    "translation started",
    "translated",
    "uploaded",
    "closed",
    "error",
  ];

  type DocumentData = {
    documents: any[]
  };

  const fetchData = async (page, pageSize, sortBy, sortOrder, filters) => {
    setLoading(true);
    try {
      const documents = await getDocuments(page, pageSize, sortBy, sortOrder, filters);
      setData({
        documents: (documents.items || []).map((doc: any) => ({
          ...doc,
          status: normalizeDashboardStatus(doc.status),
        })),
      });
      setTotalDocuments(documents.total);
    } catch (error) {
      console.error("Failed to fetch documents:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage, pageSize, sortInfo.field, sortInfo.order, filters);
  }, [currentPage, pageSize, sortInfo, filters]);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const documents = await getDocuments(currentPage, pageSize, sortInfo.field, sortInfo.order, filters);
      setData({
        documents: (documents.items || []).map((doc: any) => ({
          ...doc,
          status: normalizeDashboardStatus(doc.status),
        })),
      });
    } catch (error) {
      console.error("Failed to fetch documents:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    // Ensure sorter is properly defined
    const sortBy = sorter.field || "creation_timestamp";
    const sortOrder = sorter.order === "ascend" ? "asc" : "desc";

    // Update sorting and pagination in state
    setSortInfo({ field: sortBy, order: sortOrder });
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);

    // Ensure filters are properly updated
    let updatedFilters = {};
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        updatedFilters[key] = filters[key];
      }
    });

    setFilters(updatedFilters);

    // Call fetchData AFTER state updates
    setTimeout(() => {
      fetchData(pagination.current, pagination.pageSize, sortBy, sortOrder, updatedFilters);
    }, 50); // Small delay to ensure state is updated
  };

  useEffect(() => {
    // Auto-refresh the table every 5 minutes
    const intervalId = setInterval(() => {
      handleRefresh();
    }, 300000); // 300000 ms = 5 minutes

    // Clean up the interval when the component is unmounted
    return () => {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = () => {
    const rows = data.documents;
    if (rows && rows.length) {
      const csv = Papa.unparse(rows);
      // Create a blob of the data
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

      // Create a link element, use it to download the blob, and revoke the URL
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", "Patren_table_data.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleOpenDocument = (record: any) => {
    navigate(`/document2/${record.uuid}`);
  };

  const copyUuidToClipboard = async (uuid: string) => {
    try {
      await navigator.clipboard.writeText(uuid);
      message.success('UUID copiato nella clipboard!');
    } catch (err) {
      // Fallback per browser più vecchi
      try {
        const textArea = document.createElement('textarea');
        textArea.value = uuid;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        message.success('UUID copiato nella clipboard!');
      } catch (fallbackErr) {
        message.error('Errore nel copiare UUID nella clipboard');
        console.error("Errore fallback:", fallbackErr);
      }
    }
  };

  const downloadBlobFrom = async (endpoint: string, record: any) => {
    const uuid = record.uuid;
    setLoading(true);
    try {
      const response = await axios.post(
        `${getEndpoint('BACKEND')}/${endpoint}/${uuid}`,
        {},
        {
          headers: { "Content-Type": "application/json" },
          responseType: "blob",
        }
      );

      if (response.status === 200) {
        const contentDisposition = response.headers['content-disposition'];
        let filename = 'downloaded_file';
        if (contentDisposition) {
          const parts = contentDisposition.split(';');
          const filenamePart = parts.find(part => part.trim().startsWith('filename='));
          if (filenamePart) {
            filename = filenamePart.split('=')[1].trim().replace(/"/g, '');
          }
        }

        const blob = new Blob([response.data], {
          type: response.headers['content-type'],
        });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        link.click();

        message.success("Document ready for download.");
      } else {
        message.error("Failed to download the document.");
      }
    } catch (error) {
      console.error("Error:", error);
      message.error("An error occurred while downloading the document.");
    } finally {
      setLoading(false);
    }
  };

  const DownloadFile = (record: any) => downloadBlobFrom("downloadTranslation", record);
  const DownloadExcel = (record: any) => downloadBlobFrom("downloadExcel", record);

  const sendByEmail = async (record: any) => {
    const uuid = record.uuid;
    setLoading(true);
    try {
      const response = await axios.post(`${getEndpoint('BACKEND')}/sendMail/${uuid}`);
      if (response.status === 200) {
        message.success("Document sent via email.");
      } else {
        message.error("Failed to send the document via email.");
      }
    } catch (error) {
      console.error("Error:", error);
      message.error("An error occurred while sending the document via email.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteModal = (record: any) => {
    setCurrentRecord(record);
    setIsModalVisible(true);
  };

  const handleDeleteConfirm = async (record: any) => {
    try {
      await axios.delete(`${getEndpoint("BACKEND")}/delete/${record.uuid}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      message.success("Record deleted.");
      setIsModalVisible(false);
      handleRefresh();
    } catch (error) {
      message.error("Failed to delete record.");
      setIsModalVisible(false);
    }
  };

  const handleResetModal = (record: any) => {
    setCurrentResetRecord(record);
    setIsResetModalVisible(true);
  };

  const handleResetConfirm = async (record: any) => {
    try {
      await axios.post(`${getEndpoint("BACKEND")}/reset/${record.uuid}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      message.success("Translation reset.");
      setIsResetModalVisible(false);
      handleRefresh();
    } catch (error) {
      message.error("Failed to reset translation.");
      setIsResetModalVisible(false);
    }
  };

  const tableColumns: ColumnType<any>[] = [
      {
        title: "Client",
        dataIndex: "client",
        key: "client",
        sorter: true,
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
          <Input
            placeholder="Filter by client"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
          />
        ),
      },
      {
        title: "Job Name",
        dataIndex: "job_name",
        key: "job_name",
        sorter: true,
        render: (jobName: any, record: any) => (
          <Tooltip title="Clicca per copiare l'UUID">
            <span
              style={{ cursor: "pointer" }}
              onClick={() => copyUuidToClipboard(record.uuid)}
            >
              {jobName || record.jobname || ""}
            </span>
          </Tooltip>
        ),
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
          <Input
            placeholder="Filter by Job Name"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
          />
        ),
      },
      {
        title: "Document Name",
        dataIndex: "document_name",
        key: "document_name",
        sorter: true,
        render: (_: any, record: any) => {
          const link = record.document_link;
          const name = record.document_name || "";
          if (link) {
            return (
              <a href={link} target="_blank" rel="noopener noreferrer">
                {name}
              </a>
            );
          }
          return name;
        },
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
          <Input
            placeholder="Filter by Document Name"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
          />
        ),
      },
      {
        title: "Direction",
        dataIndex: "direction",
        key: "direction",
        width: 100,
        sorter: true,
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
          <Input
            placeholder="Filter by Direction"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
          />
        ),
        onFilter: (value, record) => {
          const direction = String(record.direction || '');
          const filterValue = String(value || '');
          return direction.toLowerCase().includes(filterValue.toLowerCase());
        },
      },
      {
        title: "Category",
        dataIndex: "category",
        key: "category",
        width: 80,
        sorter: true,
        render: (category: any) =>
          Array.isArray(category) ? category.join(", ") : category || "",
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
          <Input
            placeholder="Filter by Category"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
          />
        ),
      },
      {
        title: "Date",
        dataIndex: "creation_timestamp",
        key: "creation_timestamp",
        width: 150,
        sorter: true,
      },
      {
        title: "Rows",
        dataIndex: "content_length",
        key: "content_length",
        sorter: true,
      },
      {
        title: "Document Status",
        dataIndex: "status",
        key: "status",
        width: 150,
        filters: allStatuses.map(status => ({ text: status, value: status })),
        onFilter: (value, record) => normalizeDashboardStatus(record.status) === value,
        render: (document_status: any, record: any) => {
          const normalizedStatus = normalizeDashboardStatus(document_status);
          let color = "green";
          let text = "";
          switch (normalizedStatus) {
            case "downloading":
              color = "gray";
              text = "DOWNLOADING";
              break;
            case "translation started":
              color = "geekblue";
              text = "TRANSLATING";
              break;
            case "translated":
              color = "green";
              text = "TRANSLATED";
              break;
            case "to be uploaded":
              color = "lightgrey";
              text = "Loading";
              break;
            case "error":
              color = "volcano";
              text = "Error";
              break;
            case "ready for translation":
              color = "yellow";
              text = "Ready";
              break;
          }

          const displayText = (text || normalizedStatus || "").toUpperCase();
          const isTranslated = normalizedStatus === "translated";

          return (
            <>
              <Tag color={color}>{displayText}</Tag>
              {isTranslated && (
                <Tooltip title="Apri il Post-editing AI">
                  <Tag
                    color="purple"
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate(`/pe/${record.uuid}`)}
                  >
                    Post-editing AI
                  </Tag>
                </Tooltip>
              )}
              {record.num_error > 0 && <Tag color={"red"}>ERROR IN ROWS</Tag>}
              {record.uploaded === true && <Tag color={"lightgray"}>UPLOADED</Tag>}
              {record.closed === true && <Tag color={"lightgray"}>CLOSED</Tag>}
              {record.warning && (
                <Tooltip title={`${record.warning}${record.warning_timestamp ? ` (${record.warning_timestamp})` : ""}`}>
                  <span
                    style={{ marginLeft: 6, cursor: "help", color: "#faad14", fontSize: 13 }}
                    aria-label="warning"
                  >
                    ⚠️
                  </span>
                </Tooltip>
              )}
            </>
          );
        },
      },
    {
      title: "Progress",
      dataIndex: "progress",
      key: "progress",
      render: (value, record) => {
        if (record.content_length && record.content_length > 0) {
          return (
            <Progress
              percent={(record.num_translated / record.content_length) * 100}
              showInfo={false}
            />
          );
        }
        return <span>N/A</span>;
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      width: 80,
      render: (_text, record) => {
        const translated =
          record?.document_status === "translated" ||
          normalizeDashboardStatus(record?.status) === "translated";
        const items: MenuProps["items"] = [
          { key: "view", label: "View Rows", onClick: () => handleOpenDocument(record) },
        ];
        if (record?.fileType === "docx" && translated) {
          items.push({ key: "mail", label: "Send via mail", onClick: () => sendByEmail(record) });
          items.push({ key: "dl", label: "Download file", onClick: () => DownloadFile(record) });
        }
        if (record?.fileType === "bicolonnare-xtrf") {
          if (translated) {
            items.push({ key: "mail2", label: "Send via email", onClick: () => sendByEmail(record) });
            items.push({ key: "dl2", label: "Download file", onClick: () => DownloadFile(record) });
          }
          items.push({ key: "xls", label: "Download Excel", onClick: () => DownloadExcel(record) });
        }
        items.push({ type: "divider" });
        items.push({
          key: "reset",
          label: <span style={{ color: "orange" }}>Reset translation</span>,
          onClick: () => handleResetModal(record),
        });
        items.push({ key: "del", label: "Delete", danger: true, onClick: () => handleDeleteModal(record) });
        return (
          <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      <DeleteConfirmation
        isVisible={isModalVisible}
        onConfirm={() => handleDeleteConfirm(currentRecord)}
        onClose={() => setIsModalVisible(false)}
        record={currentRecord}
      />

      <Modal
        title="Reset Translation"
        open={isResetModalVisible}
        onOk={() => {
          if (currentResetRecord) {
            handleResetConfirm(currentResetRecord);
          }
        }}
        onCancel={() => setIsResetModalVisible(false)}
        okText="Reset"
        cancelText="Cancel"
        okType="danger"
      >
        <p>Are you sure you want to reset the translation for this document?</p>
        <p>This will set the status back and clear all translation content.</p>
        {currentResetRecord && (
          <p><strong>Document:</strong> {(currentResetRecord as any).document_name}</p>
        )}
      </Modal>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
        <Input.Search
          placeholder="Cerca: documento, job, lingua, stato, cliente, dominio..."
          allowClear
          enterButton="Cerca"
          size="large"
          style={{ maxWidth: 640, width: '100%' }}
          onSearch={(value) => {
            setCurrentPage(1);
            setFilters(value && value.trim() ? { search: value.trim() } : {});
          }}
        />
      </div>

      <div className="table-container">
        <div>
          {" "}
          <Table
            dataSource={data.documents}
            columns={tableColumns}
            loading={loading}
            onChange={handleTableChange}
              pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalDocuments,
              showSizeChanger: true,
              hideOnSinglePage: false,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              onChange: (page, pageSize) => {
                setCurrentPage(page);
                setPageSize(pageSize);
                // fetchData is now only called by useEffect, not here
              },
            }}
          />
          <div>
            <div className="table-container">
              <Button
                onClick={handleRefresh}
                type="primary"
                style={{ margin: 16 }}
                disabled={loading}
              >
                Refresh table
              </Button>
              <Button
                onClick={handleDownload}
                type="primary"
                style={{ margin: 16 }}
                disabled={loading}
              >
                Download Table
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
